import { CAMPAIGN_STATUS } from 'lib';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Pagination from '../../Moderation/components/Pagination';
import { Badge, Btn, Empty, GroupLabel, STATUS_LABEL, STATUS_TONE, T, Table } from './ui';

/**
 * Every campaign, with what became of it — beside the scheduler that gives them their times.
 *
 * It sat on the dashboard, one navigation away from the panel that schedules a send, and that is
 * the wrong side of the boundary: "did the thing I just scheduled land" is asked at the moment of
 * scheduling, and answering it meant leaving the page that asked. So the list is now the
 * scheduler's right-hand column, where the design puts a queue.
 *
 * It replaces a read-only `DispatchQueue` that used to render there. That component existed only
 * because this list was elsewhere — it showed the same rows, minus drafts, minus every action, and
 * having both here would be two tables of the same campaigns a column apart. The full list answers
 * the queue's question and the dashboard's: live work sorts to the top, so what is queued is what
 * is read first.
 *
 * Read-only for now. Opening a campaign and archiving one are both built and both commented out at
 * the operator's request, which leaves Cancel and Stop repeating — the two that stop something
 * before it reaches a fan, and the two worth having where the scheduling happens. Everything that
 * changes what a campaign *says* lives in the builder, and everything that changes a template lives
 * in Templates.
 *
 * It renders the design's four columns and nothing more: name, scheduled date, audience, status.
 * The delivered/failed counts had a `Result` column here and the design has no room for one, so
 * they are read on Reports, which is the page that exists to answer how a send went.
 */

/** Occurrences of a series are folded under their parent rather than filling the list. */
const isOccurrence = (campaign) => Boolean(campaign.recurringParent);

/*
  Five rows a page, at the owner's request (it was ten).

  Paged here rather than by the server, because the order is decided here: live work first,
  occurrences folded under their series, archived rows left out. A server page of five would be five
  rows before that folding — fewer than ten on screen, in the wrong order, with each series' send
  count taken from only the occurrences that happened to land on the same page.
*/
const PAGE_SIZE = 5;

const LIVE = [CAMPAIGN_STATUS.DRAFT, CAMPAIGN_STATUS.SCHEDULED, CAMPAIGN_STATUS.SENDING];

/** A repeating series that will still send, which is live work like a scheduled campaign. */
const isActiveSeries = (campaign) => Boolean(campaign.recurrence?.active);

const isLive = (campaign) => LIVE.includes(campaign.status) || isActiveSeries(campaign);

/*
  Cancel is offered up to the moment sending starts, and not after.

  That mirrors the backend exactly: cancelCampaign accepts draft and scheduled and refuses
  `sending`, because once recipients have started receiving it a cancel would leave a half-sent
  campaign — worse than either outcome. Offering the button anyway would promise something the API
  will refuse.
*/
const CANCELLABLE = [CAMPAIGN_STATUS.DRAFT, CAMPAIGN_STATUS.SCHEDULED];

/*
  The send time, in the campaign's own zone.

  `listCampaigns` returns `scheduledAtUtc`, `scheduledLocal` and `scheduledTimezone` — and never
  `scheduledAt`, which is what this read for. So every row said "Not scheduled", including rows
  scheduled for tomorrow, and the sort tiebreak below compared the same epoch for all of them.

  `scheduledLocal` is preferred because the server has already formatted it in the zone the
  operator chose; falling back to the UTC instant renders in the *reader's* zone, which is wrong
  but visible, where showing nothing was wrong and invisible.
*/
function formatWhen(campaign) {
  if (campaign.scheduledLocal) return campaign.scheduledLocal;

  /*
    A repeating series: its next send.

    The series itself never has a send time, and each occurrence only becomes a campaign in the
    minute it goes out, so this said "Not scheduled" beside every repeating campaign. The server
    formats the next few sends in the series' own zone, like `scheduledLocal`.
  */
  if (campaign.upcomingLocal?.length) return campaign.upcomingLocal[0];

  if (!campaign.scheduledAtUtc) return null;

  return new Date(campaign.scheduledAtUtc).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/*
  The design's own table, drawn rather than approximated.

  A bordered, rounded panel with a tinted header strip, uppercase column captions and hairline row
  rules — which is what this had to become once it moved into the frame the dispatch queue used to
  fill. `Table` in `ui.js` supplies the type and the rules; what it does not supply is the frame
  around them, because most tables on these screens sit directly on a card and do not want one.

  It is also the scroll container. The actions on the right are optional per row, so on a narrow
  column the table can outgrow it, and a rounded box has to clip its own overflow or the corners
  cut through the first and last rows.
*/
const Framed = styled.div`
  border: 1px solid ${T.border};
  border-radius: 12px;
  background: ${T.card};
  overflow: auto;

  /*
    A step smaller than the shared table, at the owner's request: this list sits in the narrower
    half of the scheduler, and at the shared 13.5px its names and dates wrapped onto three lines.
    Scoped here, so the dashboard's and the reports' tables keep their size.
  */
  table {
    font-size: 12.5px;
  }

  thead th {
    background: ${T.panel};
    border-bottom: 1px solid ${T.border};
    padding: 11px 10px;
    font-size: 10.5px;
  }

  tbody td {
    padding: 12px 10px;
  }

  /*
    The name takes the slack; the rest shrink to what they hold, as the design packs them right.

    The 1% is doing the work, and leaving it out is what clipped the status badge off the right
    edge in the first place: a first column asking for 100% is given the whole frame, and the four
    columns after it are then laid out *beyond* it. 1% is below every one of their max-content
    widths, so each is rounded back up to fit its own text and the first column is left with
    whatever remains — which is the only column with anything to gain from more room.

    Measured rather than reasoned: at an 880px column the name gets 376px and nothing is cut, and
    it still fits exactly at 668px, which is this column at a 1500px window — the narrowest the
    two-column layout ever is.
  */
  th:first-child,
  td:first-child {
    width: 100%;
  }

  th:not(:first-child),
  td:not(:first-child) {
    width: 1%;
  }
`;

/*
  The scheduled instant, and the zone it is read in, on two lines.

  They were one — `16 Sept 2026, 19:45 (Asia/Dhaka)` — and that single cell was wider than the
  three columns beside it put together, so the table outgrew its half of the scheduler and the
  status badge was clipped off the right edge.

  Stacking them is not a compromise between showing the zone and fitting: the zone is the quieter
  half of the answer and reads that way. Dropping it was the other option and it is not one — a
  bare `19:45` beside a campaign aimed at another continent is the ambiguity the timezone select
  three fields to the left exists to remove.
*/
const Zone = styled.div`
  margin-top: 3px;
  font-size: 11px;
  color: ${T.faint};
`;

/*
  Wrapping, rather than buttons held on one line by `white-space: nowrap`.

  A scheduled repeating campaign offers Cancel and Stop repeating together, and in a narrow column
  that pair was wider than the cell — so it pushed the horizontal scrollbar and the actions sat
  off-screen on the one row where they matter most.
*/
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

/** The list's badges, a step down with its text. */
const SMALL_BADGE = { fontSize: 11, padding: '3px 8px' };

const CampaignList = ({ campaigns = [], onOpen, onArchive, onCancel, onStopRecurrence, busy }) => {
  /*
    Archived campaigns are simply not listed.

    There were Active / All / Archived tabs here, and they are gone at the operator's request. That
    leaves one question to answer rather than three, and `active` was the tab this always opened on
    — so what is on screen does not change, only the ability to switch away from it. `listCampaigns`
    is still asked for archived rows, because the shell's other readers count them.
  */
  const rows = useMemo(
    () =>
      campaigns
        .filter((campaign) => !isOccurrence(campaign) && !campaign.archivedAt)
        // Live work first, then the most recent history — an operator opening this wants to see
        // what is about to go out before what already has.
        .sort((a, b) => {
          const aLive = isLive(a);
          const bLive = isLive(b);
          if (aLive !== bLive) return aLive ? -1 : 1;
          return new Date(b.scheduledAtUtc || 0) - new Date(a.scheduledAtUtc || 0);
        }),
    [campaigns]
  );

  /** How many occurrences a series has produced, shown on the parent row. */
  const occurrenceCount = useMemo(() => {
    const counts = new Map();
    campaigns.filter(isOccurrence).forEach((campaign) => {
      const parent = String(campaign.recurringParent);
      counts.set(parent, (counts.get(parent) || 0) + 1);
    });
    return counts;
  }, [campaigns]);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // Clamped rather than reset, so cancelling the last row on a page lands on the page before it.
  const current = Math.min(page, totalPages);
  const pageRows = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <GroupLabel>{`${rows.length} campaign${rows.length === 1 ? '' : 's'}`}</GroupLabel>

      {rows.length === 0 ? (
        <Empty title="No campaigns yet">
          Campaigns appear here as soon as a draft is saved in the builder above.
        </Empty>
      ) : (
        <Framed>
          <Table aria-label="Campaigns">
            <thead>
              <tr>
                <th>Campaign Name</th>
                <th>Scheduled Date</th>
                <th>Audience</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((campaign) => {
                const id = campaign.id || campaign._id;
                const when = formatWhen(campaign);
                const zone = when
                  ? campaign.scheduledTimezone || campaign.recurrence?.timezone
                  : null;
                // The sends after the next one, so an operator can see the series will keep going.
                const later = campaign.scheduledLocal
                  ? []
                  : (campaign.upcomingLocal ?? []).slice(1);
                const occurrences = occurrenceCount.get(String(id)) || 0;

                return (
                  <tr key={id}>
                    <td style={{ color: T.heading, fontWeight: 600 }}>
                      {campaign.name}
                      {/*
                        A series says so even before it has fired. The occurrence count alone left
                        a newly-created repeating campaign looking exactly like a one-off, which is
                        the moment it matters most — it is still stoppable.
                      */}
                      {campaign.recurrence?.cadence ? (
                        <Badge
                          tone={campaign.recurrence.active ? 'blue' : 'slate'}
                          style={{ marginLeft: 8, ...SMALL_BADGE }}
                        >
                          {campaign.recurrence.active
                            ? `Repeats ${campaign.recurrence.cadence}`
                            : `Repeat stopped (${campaign.recurrence.cadence})`}
                        </Badge>
                      ) : null}
                      {occurrences > 0 ? (
                        <span style={{ color: T.faint, fontWeight: 400 }}>
                          {` · ${occurrences} send${occurrences === 1 ? '' : 's'} so far`}
                        </span>
                      ) : null}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', color: T.muted }}>
                      {when || <span style={{ color: T.faint }}>Not scheduled</span>}
                      {zone ? <Zone>{zone}</Zone> : null}
                      {later.length ? <Zone>{`Then ${later.join(' · ')}`}</Zone> : null}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', color: T.heading, fontWeight: 500 }}>
                      {`${(campaign.estimatedRecipients ?? 0).toLocaleString()} users`}
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[campaign.status] || 'slate'} style={SMALL_BADGE}>
                        {STATUS_LABEL[campaign.status] || campaign.status}
                      </Badge>
                    </td>
                    <td>
                      <Actions>
                        {/*
                          Open and Archive are held back for now, at the operator's request, and
                          commented rather than deleted because both are wired end to end: `onOpen`
                          loads the row into the builder above, and `onArchive` calls
                          `setCampaignArchived`. Uncommenting either restores it as it was.

                          Restore went with Archive — they are the two halves of one control, and
                          the list no longer shows archived rows for it to act on anyway.

                          <Btn variant="outline" onClick={() => onOpen(campaign)}>
                            Open
                          </Btn>
                        */}
                        {CANCELLABLE.includes(campaign.status) ? (
                          <Btn
                            variant="outline"
                            onClick={() => onCancel(id)}
                            disabled={busy}
                            title="Stops it before it sends. Once sending has begun it is too late — some recipients already have it."
                          >
                            Cancel
                          </Btn>
                        ) : null}
                        {campaign.recurrence?.active ? (
                          <Btn
                            variant="danger"
                            onClick={() => onStopRecurrence(id)}
                            disabled={busy}
                            title="Stops future occurrences. Occurrences already sent are unaffected, and one already queued still goes out — cancel that separately."
                          >
                            Stop repeating
                          </Btn>
                        ) : null}
                      </Actions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Framed>
      )}

      {rows.length > PAGE_SIZE ? (
        <Pagination
          page={current}
          totalPages={totalPages}
          onPrev={() => setPage(current - 1)}
          onNext={() => setPage(current + 1)}
          onGoToPage={setPage}
        />
      ) : null}
    </div>
  );
};

export default CampaignList;
